import { expVal } from '@atlassian/jira-feature-experiments';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import type { RouteName } from '@atlassian/jira-route-name-types/src/RouteName.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { FREE_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import {
	useIsSimplifiedProject,
	useProjectContext,
} from '@atlassian/jira-providers-project-context/src/index.tsx';

export const NAV3_TMP_PROJECT_VIEW_ROUTES = new Set<RouteName>([
	'software-boards',
	'software-backlog',
	'software-timeline',
]);

export const NAV4_TMP_PROJECT_VIEW_ROUTES = new Set<RouteName>([
	'software-archived-work-items',
	'software-backlog',
	'software-boards',
	'software-calendar',
	'software-code',
	'software-deployments',
	'software-form',
	'software-form-builder',
	'software-form-submit',
	'software-goals',
	'software-incidents',
	'software-issue-navigator',
	'software-list',
	'software-release-notes',
	'software-reports',
	'software-roadmap',
	'software-security',
	'software-summary',
	'software-timeline',
]);

/**
 * We want to show the split share button when the following conditions are met:
 * - All nav4 views OR board/backlog/timeline in nav3 for TMP
 * - User is a Jira admin or project admin
 * - Split share button experiment is enabled
 *
 * @returns {boolean} Whether the split share button is enabled
 */
export const useIsSplitShareButtonEnabled = (): boolean => {
	const isAdmin = useIsAdmin();
	const { data: projectContextData } = useProjectContext();
	const isProjectAdmin = !!projectContextData?.isProjectAdmin;
	const isTMP = useIsSimplifiedProject();
	const appEditions = useAppEditions();

	const projectType = projectContextData?.projectType;
	const isPaidSoftwareEdition = projectType === 'software' && appEditions.software !== FREE_EDITION;
	const isPaidBusinessEdition = projectType === 'business' && appEditions.core !== FREE_EDITION;

	const shouldShowInNav3View = useIsActiveRoute(NAV3_TMP_PROJECT_VIEW_ROUTES);
	const shouldShowInNav4View = useIsActiveRoute(NAV4_TMP_PROJECT_VIEW_ROUTES);

	return (
		((getWillShowNav4() && shouldShowInNav4View) || shouldShowInNav3View) &&
		(isPaidSoftwareEdition || isPaidBusinessEdition) &&
		isTMP &&
		(isAdmin || isProjectAdmin) &&
		expVal('split_share_button_for_tmp_admins', 'isEnabled', false)
	);
};
